// src/utils/organizationRuntime.js
//
// Pass 82C — the SMALLEST organization-level runtime context so generic operational code stops silently assuming
// "current organization == SAS Niseko". Generic runtime resolves timezone / current-season / category display labels
// FROM THE ORGANIZATION (an org doc or the selected-org context), never from a hidden SAS default. SAS-specific SOURCE
// adapters (sas_master_schedule/grid/collective parsers) are unaffected — they may still speak SAS.
//
// This is NOT a configuration framework: no finance/pricing/source config lives here.
import { CURRENT_SEASON_ID, resolveInstructorCategory, CATEGORY } from './instructorCategory';

// Deliberate NEUTRAL fallback when an org has not declared a timezone — never 'Asia/Tokyo' (that is a SAS value, and
// belongs on the SAS org doc, not in generic code).
export const DEFAULT_ORG_TIMEZONE = 'UTC';

export function resolveOrgTimezone(org, fallback = DEFAULT_ORG_TIMEZONE) {
  const tz = (org && (org.timezone || org.timeZone)) || '';
  return tz || fallback;
}

// The org's current/default operating season. Falls back to the platform global (`CURRENT_SEASON_ID`) only when the
// org has not declared one — so SAS (no field yet) and the pilot both currently resolve to 2026_27, but two orgs CAN
// resolve different seasons purely from their own docs, with NO module-global mutation.
export function resolveOrgSeasonId(org, fallback = CURRENT_SEASON_ID) {
  return (org && (org.currentSeasonId || org.seasonId)) || fallback;
}

// Tenant-neutral default category display labels. An org may override via `org.categoryLabels`
// (SAS sets { school:'SAS School', collective:'Collective' }); any org that does not gets neutral wording — so the
// pilot never displays "SAS School". The STORED category ('school'/'collective'/'sas_school'/'sas_collective') is
// unchanged — this only affects presentation.
const NEUTRAL_CATEGORY_LABELS = { school: 'Teaching Team', collective: 'External Network', unclassified: 'Needs review' };

export function orgCategoryLabels(org) {
  const c = (org && org.categoryLabels) || {};
  return {
    school: c.school || NEUTRAL_CATEGORY_LABELS.school,
    collective: c.collective || NEUTRAL_CATEGORY_LABELS.collective,
    unclassified: c.unclassified || NEUTRAL_CATEGORY_LABELS.unclassified,
  };
}

// Org-aware category display for a member (STORED category unchanged). SAS org → 'SAS School'/'Collective' (from its
// categoryLabels); pilot / any org without categoryLabels → neutral 'Teaching Team'/'External Network'.
export function categoryLabelForMember(member, season, org) {
  const labels = orgCategoryLabels(org);
  const cat = resolveInstructorCategory(member || {}, season ? { season } : {});
  if (cat === CATEGORY.SCHOOL) return labels.school;
  if (cat === CATEGORY.COLLECTIVE) return labels.collective;
  return labels.unclassified;
}

// A short human tenant identity for generic banners/headers (never a hard-coded 'sas-niseko').
export function orgDisplayName(org) {
  return (org && (org.name || org.displayName || org.organizationName || org.organizationId || org.id)) || '';
}
